import * as React from 'react';

import { cn } from '@mint-lib/tw-utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { Avatar as AvatarPrimitive } from 'radix-ui';

import { getUserInitials } from '../utils/avatar.utils.js';

const avatarVariants = cva(
  'group/avatar relative flex shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-border-primary text-text-secondary before:absolute before:block before:overflow-hidden before:rounded-full before:opacity-0 before:content-[""] hover:before:opacity-100',
  {
    variants: {
      color: {
        primary: 'bg-ui-primary hover:before:bg-ui-hover',
        secondary: 'bg-ui-secondary hover:before:bg-ui-hover',
      },
      size: {
        xsmall: [
          'h-4 w-4 text-[8px] leading-3 font-normal before:h-4 before:w-4',
        ],
        small: [
          'h-6 w-6 text-[11px] leading-3 font-normal before:h-6 before:w-6',
        ],
        medium: [
          'h-8 w-8 text-[14px] leading-4 font-normal before:h-8 before:w-8',
        ],
        large: [
          'h-10 w-10 text-[14px] leading-4 font-normal before:h-10 before:w-10',
        ],
        mega: [
          'h-20 w-20 text-[32px] leading-10 font-medium before:h-20 before:w-20',
        ],
      },
      disabled: {
        false: null,
        true: ['hover:before:opacity-0'],
      },
    },

    compoundVariants: [
      {
        color: 'primary',
        disabled: true,
        className:
          'border-border-disabled text-text-disabled hover:border-border-disabled hover:bg-ui-primary hover:text-text-disabled',
      },
      {
        color: 'secondary',
        disabled: true,
        className:
          'border-none text-text-disabled hover:border-none hover:bg-ui-secondary hover:text-text-disabled',
      },
    ],
    defaultVariants: {
      color: 'primary',
      size: 'large',
      disabled: false,
    },
  },
);

function Avatar({
  className,
  size,
  color,
  disabled,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root> &
  VariantProps<typeof avatarVariants>) {
  return (
    <AvatarPrimitive.Root
      className={cn(avatarVariants({ size, color, className, disabled }))}
      {...props}
    />
  );
}

function AvatarImage({
  className,
  disabled,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image> & {
  disabled?: VariantProps<typeof avatarVariants>['disabled'];
}) {
  return (
    <AvatarPrimitive.Image
      className={cn(
        'aspect-square border-none group-hover/avatar:opacity-80 before:bg-ui-hoverSelected',
        disabled && 'opacity-40',
        className,
      )}
      {...props}
    />
  );
}

function AvatarFallback({
  className,
  firstName,
  lastName,
  email,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback> & {
  firstName?: string;
  lastName?: string;
  email?: string;
}) {
  const userInitials = (
    getUserInitials(firstName, lastName) || email?.slice(0, 2)
  )?.toUpperCase();
  return (
    <AvatarPrimitive.Fallback
      className={cn(
        'flex h-full w-full items-center justify-center rounded-full',
        className,
      )}
      {...props}
    >
      {userInitials}
    </AvatarPrimitive.Fallback>
  );
}

export { Avatar, AvatarFallback, AvatarImage };
